import React, {useCallback, useContext, useEffect, useState} from 'react';
import {CallRow} from "../../graphql/subscription/callRow";
import {gql, QueryResult, useQuery} from "@apollo/client";
import {OAuthContext} from "../oauth/Provider";
import {
    ArrowBottomLeftIcon,
    ArrowTopRightIcon,
    ExclamationTriangleIcon,
    ExternalLinkIcon,
    InfoCircledIcon,
} from "@radix-ui/react-icons";
import {Avatar, AvatarFallback} from "@/components/ui/avatar";
import {Button} from "@/components/ui/button";
import {Spinner} from "@/components/ui/spinner";
import {callSearch, callSearchVariables} from "./types/callSearch";
import {GlobalContext} from "../../../state/GlobalProvider";
import jwt_decode from 'jwt-decode';
import {useParams} from "react-router-dom";

interface ActivityItemProps {
    status: CallRow
}

const SEARCH_QUERY = gql`query callSearch($integration: ID, $token: ID!, $audience: ID!, $query: String!) {
  calls {
    search(integration: $integration, token: $token, audience: $audience, query: $query) {
      id
      name
      detailUri
    }
  }
}`

const renderAction = (
    result: QueryResult<callSearch, callSearchVariables>,
    handleOpen: (data?: callSearch) => void,
) => {
    const {loading, data, error} = result;
    if (loading) {
        return <Spinner className="h-4 w-4 text-muted-foreground" />;
    }
    if (error) {
        return <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />;
    }
    if (data?.calls?.search?.id) {
        return (
            <Button
                variant="ghost"
                size="icon"
                aria-label="Open contact"
                title="Open contact"
                onClick={() => handleOpen(data)}
            >
                <ExternalLinkIcon className="h-4 w-4 text-primary" />
            </Button>
        );
    }
    return <InfoCircledIcon className="h-4 w-4 text-muted-foreground" aria-label="No match" />;
}

const renderName = (status: CallRow) => {
    return (status.callDirection === 'outbound') ?
        status.callerIdNumber + " - " + status.callerIdName :
        (status.calleeIdNumber || status.callerDestinationNumber) + " - " + status.calleeIdName;
}

// Returns 10 digit numeric only for search
const formatQuery = (callerIdNumber: string | null): string => {
    if (!callerIdNumber) {
        return ""
    }
    return callerIdNumber.replace(/[^0-9]/g, "").slice(-10)
}

export default function ActivityItem({status}: ActivityItemProps) {
    const globalCtx = useContext(GlobalContext);
    const {integration} = useParams<{ integration: string }>()
    const { token } = useContext(OAuthContext);
    const [ windowHandle, setWindowHandle ] = useState<Window | null>(null);
    let audience = '';
    try {
        const jwt = jwt_decode<{ aud: string }>(globalCtx.auth.token || "");
        audience = jwt.aud;
    } catch (e) {}


    const result = useQuery<callSearch, callSearchVariables>(SEARCH_QUERY, {
            variables: {
                integration,
                token: token?.accessToken || "",
                audience,
                query: formatQuery((status.callDirection === 'outbound') ? status.callerIdNumber : status.calleeIdNumber || status.callerDestinationNumber)
            }});
    const callState = status.channelCallState;

    const handleOpen = useCallback((data?: callSearch) => {
        if (data?.calls?.search?.detailUri) {
            if (!windowHandle || windowHandle.closed) {
                setWindowHandle(window.open(data.calls.search.detailUri));
            } else {
                // Window exists and is not closed, try to focus
                windowHandle.focus();
            }
        }
    },[windowHandle, setWindowHandle]);

    // Open in new window if status is active and id was returned
    useEffect(() => {
        if (callState === 'ACTIVE' && result.data?.calls?.search?.id) {
            handleOpen(result.data);
        }
    }, [callState, result.data]);

    return (
        <li className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-accent/5">
            <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--brand-green))] via-[hsl(var(--brand-orange))] to-[hsl(var(--brand-rose))] text-white">
                    {status.callDirection === 'outbound'
                        ? <ArrowBottomLeftIcon className="h-4 w-4" aria-label="Outbound" />
                        : <ArrowTopRightIcon className="h-4 w-4" aria-label="Inbound" />}
                </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                    {renderName(status)}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                    {status.held ? 'HELD' : status.channelCallState}
                </p>
            </div>
            <div className="flex items-center gap-1">
                {renderAction(result, handleOpen)}
            </div>
        </li>
    );
}
